import { MutableRefObject, RefObject, useEffect, useRef } from 'react';

// Keeps a scrolled section fixed in the viewport while a Carbon `SidePanel` in
// `slideIn` mode animates open or closed.
//
// A slide-in panel sets `margin-inline-end` on its `selectorPageContent` (here
// the shared `.do-structuring-shell` / `.afe-structuring-shell` wrapper), which
// narrows EVERY section on the page. Sections above the one in view reflow
// taller as they wrap, and because the window keeps its absolute scroll offset
// the section the user was working on is shoved out of view — e.g. opening the
// Interest edit/add panel scrolled the page up to the Wells section. Closing the
// panel does the reverse.
//
// The anchor target (the section's viewport-top) must be captured at CLICK time,
// before the toggle: React runs the child SidePanel's margin effect before this
// parent effect, so by the time an effect could read the position the push has
// already happened. The caller records the pre-toggle top into `anchorTop` (see
// the `captureAnchor` helper at the call site); this hook then re-asserts it on
// every animation frame for the duration of the transition so the section stays
// put while the surrounding layout narrows or widens around it.
export function useSlideInScrollAnchor(
  active: boolean,
  ref: RefObject<HTMLElement>,
  anchorTop: MutableRefObject<number | null>
): void {
  // Skip the initial mount: there is no open/close transition to absorb yet,
  // only react to actual toggles of `active`.
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return undefined;
    }
    const el = ref.current;
    const target = anchorTop.current;
    if (!el || target === null) return undefined;

    let raf = 0;
    // Carbon's slide-in margin transition is `moderate02` (~240ms); correct for
    // a slightly longer window so the final resting position is exact even if
    // the animation runs a touch long.
    const startTime = performance.now();
    const tick = (now: number) => {
      const delta = el.getBoundingClientRect().top - target;
      if (Math.abs(delta) >= 0.5) window.scrollBy(0, delta);
      if (now - startTime < 300) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, ref, anchorTop]);
}
